interface Props {
  scoreWidth: number;
  maxScore?: number;
}

export enum HeatLabel {
  LOW = 'LOW',
  MED = 'MED',
  HIGH = 'HIGH',
  VERY_HIGH = 'VERY_HIGH',
  EXTREME = 'EXTREME',
}

interface HeatData {
  textColorClass: string;
  bgColorClass: string;
  label: HeatLabel;
}

/**
 * Gets the color classes and label based on normalized score (0-100)
 * Uses distinct color buckets for clear visual separation
 *
 * Buckets (evenly distributed):
 * - 0-20 (Low): Light Gray
 * - 21-40 (Med): Yellow
 * - 41-60 (High): Orange
 * - 61-80 (Very High): Deep Orange
 * - 81-100 (Extreme): Red
 */
function getHeatData(normalizedScore: number): HeatData {
  if (normalizedScore <= 20) {
    return {
      textColorClass: 'text-heat-low',
      bgColorClass: 'bg-heat-low',
      label: HeatLabel.LOW,
    };
  }

  if (normalizedScore <= 40) {
    return {
      textColorClass: 'text-heat-med',
      bgColorClass: 'bg-heat-med',
      label: HeatLabel.MED,
    };
  }

  if (normalizedScore <= 60) {
    return {
      textColorClass: 'text-heat-high',
      bgColorClass: 'bg-heat-high',
      label: HeatLabel.HIGH,
    };
  }

  if (normalizedScore <= 80) {
    return {
      textColorClass: 'text-heat-very-high',
      bgColorClass: 'bg-heat-very-high',
      label: HeatLabel.VERY_HIGH,
    };
  }

  return {
    textColorClass: 'text-heat-extreme',
    bgColorClass: 'bg-heat-extreme',
    label: HeatLabel.EXTREME,
  };
}

export default function HeatScoreBadge({ scoreWidth, maxScore = 100 }: Props) {
  const normalizedScore = Math.round((scoreWidth / maxScore) * 100);
  const clampedScore = Math.max(0, Math.min(100, normalizedScore));

  const { textColorClass, bgColorClass } = getHeatData(clampedScore);

  return (
    <div className={`relative inline-flex items-center text-xs font-semibold ${textColorClass}`}>
      <span className={`w-[3px] h-4 mr-1.5 rounded-sm ${bgColorClass}`} />
      <span>{clampedScore}</span>
    </div>
  );
}
